import { Button, Input, Select } from '@imprivata-cloud/components';
import { Form, Upload } from 'antd';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, UndoOutlined } from '@ant-design/icons';
import type { FormProps } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import { useErrorContext } from '../../errorHandler/context/ErrorContext';
import useLegalFilesSave from '../../setup/hooks/useLegalFilesSave';
import { type ApiError, AppError } from '../../errorHandler/errors';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UploadFile } from 'antd/lib';
import { ErrorCode } from '../../errorHandler/constants';
import useConsentLocationSave from '../../portal/hooks/useConsentLocationSave';
import { type CustomerAgreementInfoGetResponse, DocumentPurpose } from '../../api/types';
import { getCustomerAgreementInfo } from '../../api/portalServices';
import type { DefaultOptionType } from 'antd/es/select';
import { useDeleteDocument } from '../../portal/hooks/useDeleteDocument';
import './LegalFileUpload.less';

enum FieldNames {
  CUSTOMER_CONSENT = 'consentFile',
  CUSTOMER_NOTICE = 'noticeFile',
  REGIONS = 'regions',
}
interface FormValues {
  [FieldNames.CUSTOMER_NOTICE]: File;
  [FieldNames.CUSTOMER_CONSENT]: File;
  [FieldNames.REGIONS]: string[];
}
const TRANSLATION_ROOT = 'portal.appearance';

export const LegalFileUpload = () => {
  const { t } = useTranslation();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const [form] = Form.useForm<FormValues>();
  const consentFormVal = Form.useWatch(FieldNames.CUSTOMER_CONSENT, form);
  const [canSubmit, setCanSubmit] = useState(false);
  const [fetchedConsentDocument, setFetchedConsentDocument] = useState<UploadFile[]>([]);
  const [fetchedNoticeDocument, setFetchedNoticeDocument] = useState<UploadFile[]>([]);
  const [fetchedConsentLocations, setFetchedConsentLocations] = useState<string[]>();
  const [residenceLocationsList, setResidenceLocationsList] = useState<DefaultOptionType[]>([]);
  const { data: documentsInfo, refetch } = useQuery<CustomerAgreementInfoGetResponse>({
    queryKey: ['legalDocuments'],
    queryFn: getCustomerAgreementInfo,
  });
  const legalFileSave = useLegalFilesSave();
  const { mutateAsync: consentLocationSave } = useConsentLocationSave();
  const { mutateAsync: deleteDocumentMutation } = useDeleteDocument();

  useEffect(() => {
    loadData(documentsInfo);
  }, [documentsInfo]);

  const handleSubmitButtonState = async () => {
    try {
      await form.validateFields();
      setCanSubmit(formHasChanged());
    } catch (error) {
      console.log('Validation failed: ', error);
      const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
      setCanSubmit(!hasErrors && formHasChanged());
    }
  };

  const formHasChanged = () => {
    const consentHasChange = !!form.getFieldValue(FieldNames.CUSTOMER_CONSENT);
    const noticeHasChange = !!form.getFieldValue(FieldNames.CUSTOMER_NOTICE);
    const selectHasChange =
      form.getFieldValue(FieldNames.REGIONS)?.toLocaleString() !== fetchedConsentLocations?.toLocaleString();
    return consentHasChange || noticeHasChange || selectHasChange;
  };

  const onFinish: FormProps<FormValues>['onFinish'] = async (formValues: FormValues) => {
    try {
      clearErrorNotifications();
      await legalFileSave.mutateAsync({
        consentFile: formValues[FieldNames.CUSTOMER_CONSENT],
        noticeFile: formValues[FieldNames.CUSTOMER_NOTICE],
      });
      await consentLocationSave({
        locations: formValues[FieldNames.REGIONS],
      });
      setCanSubmit(false);
      await refetch();
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
  };

  function getFileValueFromEvent(e: UploadChangeParam) {
    if (e?.fileList?.length > 0) {
      return e.fileList.pop()?.originFileObj;
    }
    return e?.file?.originFileObj;
  }

  const setRegion = (regions: string[]) => {
    form.setFieldValue(FieldNames.REGIONS, regions.length === 0 ? undefined : regions);
    handleSubmitButtonState();
  };

  const validateFileExtension = (file: File, accept: string | undefined) => {
    if (!accept) {
      return true;
    }

    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    const isValid = accept
      .toLowerCase()
      .split(',')
      .map((item) => item.trim())
      .some((acceptItem) => {
        // (e.g.) .html - .json - .xml
        if (acceptItem.startsWith('.')) {
          return fileName.endsWith(acceptItem);
        }
        // (e.g.) audio/* - image/* - video/*
        if (acceptItem.endsWith('/*')) {
          return fileType.startsWith(acceptItem.replace('/*', ''));
        }
        return fileType === acceptItem; // (e.g.) image/png
      });
    if (!isValid) {
      emitError(AppError.fromMessage(ErrorCode.INVALID_LEGAL_DOCUMENT, ErrorCode.INVALID_LEGAL_DOCUMENT));
      return Upload.LIST_IGNORE;
    }
    return isValid;
  };

  const generateFileForUploadComponent = (filename: string, readUrl: string): UploadFile => {
    return {
      uid: '1',
      name: filename,
      status: 'done',
      url: readUrl,
    };
  };

  const loadData = async (data?: CustomerAgreementInfoGetResponse) => {
    const consentDoc = [];
    const noticeDoc = [];
    const regions = data?.customerMandatoryResidenceLocationList.map((location) => location.shortName) ?? [];
    form.resetFields();
    if (data) {
      for (const document of data.customerAgreementInfoList) {
        const file = generateFileForUploadComponent(document.documentFilename, document.readUrl);
        if (document.documentPurpose === DocumentPurpose.CONSENT) {
          consentDoc.push(file);
        } else if (document.documentPurpose === DocumentPurpose.NOTICE) {
          noticeDoc.push(file);
        }
      }
      const locationList = data.residenceLocationList.map((location) => ({
        value: location.shortName,
        label: location.displayName,
        titleLabel: location.fullName,
      }));
      setResidenceLocationsList(locationList);
    }
    setFetchedConsentLocations(regions);
    form.setFieldValue(FieldNames.REGIONS, regions);
    setFetchedConsentDocument(consentDoc);
    setFetchedNoticeDocument(noticeDoc);
    await form.validateFields();
    setCanSubmit(false);
  };

  const resetConsentAfterDelete = () => {
    form.setFieldValue(FieldNames.CUSTOMER_CONSENT, undefined);
    setFetchedConsentDocument([]);
  };

  const resetNoticeAfterDelete = () => {
    form.setFieldValue(FieldNames.CUSTOMER_NOTICE, undefined);
    setFetchedNoticeDocument([]);
  };

  const undo = async () => {
    await refetch();
    loadData(documentsInfo);
  };
  const deleteDocument = async (formFieldName: FieldNames) => {
    const purpose = formFieldName === FieldNames.CUSTOMER_CONSENT ? DocumentPurpose.CONSENT : DocumentPurpose.NOTICE;

    const hasDocumentToDelete =
      (formFieldName === FieldNames.CUSTOMER_CONSENT && fetchedConsentDocument.length > 0) ||
      (formFieldName === FieldNames.CUSTOMER_NOTICE && fetchedNoticeDocument.length > 0);

    const resetDataAfterDelete =
      formFieldName === FieldNames.CUSTOMER_CONSENT ? resetConsentAfterDelete : resetNoticeAfterDelete;
    //Delete using API.
    if (hasDocumentToDelete) {
      try {
        await deleteDocumentMutation(purpose);
        resetDataAfterDelete();
      } catch (e) {
        emitError(new AppError(e as ApiError));
      }
    } else {
      //Delete only locally from component.
      form.setFieldValue(formFieldName, undefined);
      handleSubmitButtonState();
    }
  };

  const getDeleteModalProps = (documentType: string) => {
    return {
      title: t(`${TRANSLATION_ROOT}.org-delete-legal-file-title`, {
        documentType,
      }),
      children: t(`${TRANSLATION_ROOT}.org-delete-legal-file`, {
        documentType,
      }),
      okText: t('common.remove'),
    };
  };

  return (
    <Form
      form={form}
      aria-label="legal-file-form-section"
      className="legal-file-form"
      onFinish={onFinish}
      onValuesChange={handleSubmitButtonState}
      layout="vertical"
    >
      <h2>{t(`${TRANSLATION_ROOT}.org-consent-title`)}</h2>
      <p>{t(`${TRANSLATION_ROOT}.org-consent-subtitle`)}</p>
      <a href="/exampleFiles/Empty_biometric_document.html" download>
        <Button type="default" icon={<DownloadOutlined className="download-icon" />} size="small">
          {t(`${TRANSLATION_ROOT}.org-download-sample-file`)}
        </Button>
      </a>

      <div className="field" aria-label="customer-consent-file">
        <label htmlFor="customer-consent-file-input">{t(`${TRANSLATION_ROOT}.org-customer-consent-file`)}</label>
        <Input.File
          id="customer-consent-file-input"
          btnText={t(`${TRANSLATION_ROOT}.org-upload-file-text`)}
          accept=".html"
          customRequest={({ onSuccess }) => onSuccess?.('ok')}
          maxCount={1}
          fieldName={FieldNames.CUSTOMER_CONSENT}
          valuePropName="file"
          getValueFromEvent={getFileValueFromEvent}
          beforeUpload={(file) => validateFileExtension(file, '.html')}
          fileList={fetchedConsentDocument}
          showUploadList={false}
          onRemove={() => deleteDocument(FieldNames.CUSTOMER_CONSENT)}
          deleteModal={
            fetchedConsentDocument?.length > 0
              ? getDeleteModalProps(t(`${TRANSLATION_ROOT}.org-consent-document`))
              : undefined
          }
          disabled={!documentsInfo?.customAgreementEnabled}
        />
        <span className="caption">{t(`${TRANSLATION_ROOT}.org-legal-file-type`)}</span>
      </div>

      <div className="field" aria-label="customer-residence">
        <Form.Item
          name={FieldNames.REGIONS}
          dependencies={[FieldNames.CUSTOMER_CONSENT]}
          initialValue={fetchedConsentLocations}
          label={t(`${TRANSLATION_ROOT}.org-customer-residence`)}
        >
          {/*
            Note: admin can save consent doc and location individually.
            It is like a resumable feature for admin. It only goes into
            effect for end-user when both are present.
          */}
          <Select
            mode="multiple"
            allowClear
            options={residenceLocationsList}
            onChange={setRegion}
            defaultValue={fetchedConsentLocations}
            disabled={!documentsInfo?.customAgreementEnabled}
          />
        </Form.Item>
      </div>

      <div className="field" aria-label="customer-notice-file">
        <label htmlFor="customer-notice-file-input">{t(`${TRANSLATION_ROOT}.org-customer-notice-file`)}</label>
        <Input.File
          id="customer-notice-file-input"
          btnText={t(`${TRANSLATION_ROOT}.org-upload-file-text`)}
          accept=".html"
          customRequest={({ onSuccess }) => onSuccess?.('ok')}
          maxCount={1}
          fieldName={FieldNames.CUSTOMER_NOTICE}
          valuePropName="file"
          beforeUpload={(file) => validateFileExtension(file, '.html')}
          getValueFromEvent={getFileValueFromEvent}
          fileList={fetchedNoticeDocument}
          showUploadList={false}
          onRemove={() => deleteDocument(FieldNames.CUSTOMER_NOTICE)}
          deleteModal={
            fetchedNoticeDocument?.length > 0
              ? getDeleteModalProps(t(`${TRANSLATION_ROOT}.org-notice-document`))
              : undefined
          }
          disabled={!documentsInfo?.customAgreementEnabled}
        />
        <span className="caption">{t(`${TRANSLATION_ROOT}.org-legal-file-type`)}</span>
      </div>

      <div className="btns-container">
        <Button
          type="text"
          className="text-button"
          onClick={() => undo()}
          disabled={!canSubmit}
          icon={<UndoOutlined className="undo-icon" />}
        >
          {t('common.undo')}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={legalFileSave.isPending}
          disabled={legalFileSave.isPending || !canSubmit}
        >
          {t('common.save')}
        </Button>
      </div>
    </Form>
  );
};
